import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { apiService } from '../services/api';
import type { BuilderDetectionResult } from '../types/tenant';

export const useDetectBuilder = () => {
  return useMutation({
    mutationFn: (website: string) => apiService.detectBuilder(website),
    onError: (error: Error) => {
      message.error(`Builder detection failed: ${error.message}`);
    },
  });
};

export const useConfirmBuilderConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      website,
      config,
    }: {
      website: string;
      config: { page_builder?: string; seo_plugin?: string; theme?: string };
    }) => apiService.confirmBuilderConfig(website, config),
    onSuccess: (_data, variables) => {
      message.success('Builder configuration saved');
      queryClient.invalidateQueries({ queryKey: ['tenantSettings', variables.website] });
    },
    onError: (error: Error) => {
      message.error(`Failed to save configuration: ${error.message}`);
    },
  });
};

export const useExtractAdapterConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (website: string) => apiService.extractAdapterConfig(website),
    onSuccess: (_data, _variables) => {
      message.success('Adapter config extracted successfully');
      queryClient.invalidateQueries({ queryKey: ['tenantSettings'] });
    },
    onError: (error: Error) => {
      message.error(`Adapter extraction failed: ${error.message}`);
    },
  });
};

export const useActivateAdapter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      website,
      status,
      metaKeysOverride,
    }: {
      website: string;
      status: 'active' | 'disabled';
      metaKeysOverride?: Record<string, string>;
    }) => apiService.activateAdapter(website, status, metaKeysOverride),
    onSuccess: (_data, variables) => {
      const label = variables.status === 'active' ? 'activated' : 'disabled';
      message.success(`Adapter ${label} successfully`);
      queryClient.invalidateQueries({ queryKey: ['tenantSettings'] });
    },
    onError: (error: Error) => {
      message.error(`Failed to update adapter: ${error.message}`);
    },
  });
};

export const useInstallHelperPlugin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (website: string) => apiService.installHelperPlugin(website),
    onSuccess: (data) => {
      if (data.success) {
        message.success(data.message);
      } else if (data.manual_install_required) {
        message.warning(data.message);
      } else {
        message.error(data.message);
      }
      queryClient.invalidateQueries({ queryKey: ['tenantSettings'] });
    },
    onError: (error: Error) => {
      message.error(`Failed to install helper plugin: ${error.message}`);
    },
  });
};

export const useSeedAdapterConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      website,
      builderName,
      metaKeys,
      shortcodeWrapper,
    }: {
      website: string;
      builderName: string;
      metaKeys: Record<string, string>;
      shortcodeWrapper?: Record<string, unknown> | null;
    }) => apiService.seedAdapterConfig(website, builderName, metaKeys, shortcodeWrapper),
    onSuccess: () => {
      message.success('Adapter config seeded successfully');
      queryClient.invalidateQueries({ queryKey: ['tenantSettings'] });
    },
    onError: (error: Error) => {
      message.error(`Failed to seed adapter config: ${error.message}`);
    },
  });
};
